import React, { useRef, useState } from 'react';
import { formatDateTime } from '../../utils/formatDate';
import { getTeamMembersCache } from '../../services/teamMembersCache';
import { resolveAssigneeName } from '../../services/TasksService';
import '../../styles/components/CommentThread.css';

/**
 * A single entry in a comment thread. Every entity's comments (events,
 * tasks, vendors, guests, budget items, procurement orders) map their API
 * rows onto this shape, so the thread UI stays source-agnostic.
 */
export interface ThreadComment {
  id: string;
  author: string;
  /** Who wrote it, when known. Edit and delete are offered only to this person. */
  authorEmail?: string | null;
  body: string;
  createdAtISO: string;
  updatedAtISO?: string | null;
}

/**
 * A non-comment entry (a status change, an automated sync note, etc.) that
 * reads into the same timeline as real comments — the same idea as a
 * notifications window that can carry more than one kind of notification.
 * Rendered distinctly from a comment (no author/edit/delete), just placed
 * in chronological order alongside them instead of living in its own
 * separate section with its own heading.
 */
export interface SystemThreadEvent {
  id: string;
  timestampISO: string;
  content: React.ReactNode;
  /** Who made the change (usually an email) — rendered as its own "by
   *  {name}" header line, same treatment as a real comment's author. */
  actor?: string | null;
}

interface CommentThreadProps {
  comments: ThreadComment[];
  /** Resolves once the comment is persisted; rejects to keep the draft. */
  onPost: (body: string) => Promise<void>;
  /** Backend only allows a comment's own author to delete/edit it — pass
   *  the signed-in user's identity (their email, matching what the backend
   *  stores as `author`) so the controls only render on your own comments. */
  currentUserEmail?: string;
  onDelete?: (id: string) => Promise<void>;
  onEdit?: (id: string, body: string) => Promise<void>;
  /** Optional non-comment entries (e.g. status-change history) merged into
   *  the same chronological feed as the comments below. */
  systemEvents?: SystemThreadEvent[];
  label?: string;
  placeholder?: string;
  emptyText?: string;
  disabled?: boolean;
}

/**
 * Shared append-only comment thread — the one comments UI in the app.
 * Owns its own draft/posting state so callers only supply the list and a
 * persist callback. On a failed post the draft is deliberately kept so
 * the user does not lose what they typed.
 */
const CommentThread: React.FC<CommentThreadProps> = ({
  comments,
  onPost,
  currentUserEmail,
  onDelete,
  onEdit,
  systemEvents,
  label = 'Comments',
  placeholder = 'Add a comment…',
  emptyText = 'No comments yet.',
  disabled = false,
}) => {
  const [draft, setDraft] = useState('');
  const [posting, setPosting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const [saving, setSaving] = useState(false);

  // @mention autocomplete — driven by the same team-members cache the task
  // assignee picker reads (TasksService.ts), so there is no separate people
  // list to keep in sync. Names are matched case-insensitively at render
  // time to highlight "@Name" substrings already typed into a comment body.
  // Re-read every render rather than memoized once — team sync
  // (usePlanningSync) can populate the cache after this component has
  // already mounted, and a frozen empty snapshot would never pick that up.
  const teamMembers = getTeamMembersCache();
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionAnchor, setMentionAnchor] = useState<'draft' | 'edit'>('draft');
  const draftInputRef = useRef<HTMLTextAreaElement>(null);
  const editInputRef = useRef<HTMLTextAreaElement>(null);

  const mentionMatches = mentionQuery === null
    ? []
    : teamMembers.filter(m => m.isActive !== false && m.name.toLowerCase().includes(mentionQuery.toLowerCase())).slice(0, 6);

  // Finds the "@partial" run immediately before the cursor, if any, so the
  // dropdown only opens while actively typing a mention rather than any
  // time "@" appears earlier in the text.
  const detectMention = (text: string, cursor: number): string | null => {
    const uptoCursor = text.slice(0, cursor);
    const match = uptoCursor.match(/(?:^|\s)@([^\s@]*)$/);
    return match ? match[1] : null;
  };

  const handleDraftChange = (value: string, cursor: number) => {
    setDraft(value);
    setMentionAnchor('draft');
    setMentionQuery(detectMention(value, cursor));
  };

  const handleEditChange = (value: string, cursor: number) => {
    setEditDraft(value);
    setMentionAnchor('edit');
    setMentionQuery(detectMention(value, cursor));
  };

  const insertMention = (name: string) => {
    const isDraft = mentionAnchor === 'draft';
    const ref = isDraft ? draftInputRef.current : editInputRef.current;
    const text = isDraft ? draft : editDraft;
    const cursor = ref?.selectionStart ?? text.length;
    const uptoCursor = text.slice(0, cursor);
    const replaced = uptoCursor.replace(/@([^\s@]*)$/, `@${name} `);
    const next = replaced + text.slice(cursor);
    if (isDraft) setDraft(next); else setEditDraft(next);
    setMentionQuery(null);
    // Put the caret right after the inserted mention, not at the end of
    // the whole comment — matters once there's text after the cursor.
    requestAnimationFrame(() => {
      const el = isDraft ? draftInputRef.current : editInputRef.current;
      if (el) {
        const pos = replaced.length;
        el.focus();
        el.setSelectionRange(pos, pos);
      }
    });
  };

  // Wraps recognized "@Name" runs in a highlight span for display. Plain
  // string matching against known team member names — no stored mention
  // ids, so a later name change just stops highlighting, it never breaks.
  const renderBody = (body: string): React.ReactNode => {
    if (teamMembers.length === 0) return body;
    const names = teamMembers.map(m => m.name).filter(Boolean).sort((a, b) => b.length - a.length);
    if (names.length === 0) return body;
    const pattern = new RegExp(`@(${names.map(n => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})\\b`, 'g');
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    let key = 0;
    while ((match = pattern.exec(body)) !== null) {
      if (match.index > lastIndex) parts.push(body.slice(lastIndex, match.index));
      parts.push(<span key={key++} className="ds-comment-mention">{match[0]}</span>);
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < body.length) parts.push(body.slice(lastIndex));
    return parts;
  };

  const handleDelete = async (id: string) => {
    if (!onDelete || deletingId) return;
    setDeletingId(id);
    try {
      await onDelete(id);
    } finally {
      setDeletingId(null);
    }
  };

  const startEdit = (c: ThreadComment) => {
    setEditingId(c.id);
    setEditDraft(c.body);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft('');
  };

  const handleSaveEdit = async (id: string) => {
    const body = editDraft.trim();
    if (!body || !onEdit || saving) return;
    setSaving(true);
    try {
      await onEdit(id, body);
      setEditingId(null);
      setEditDraft('');
    } catch {
      // Keep the edit open — the caller surfaces the error to the user.
    } finally {
      setSaving(false);
    }
  };

  // Merged, chronological view of real comments + any system events —
  // built here rather than by the caller so every other consumer (events,
  // tasks, vendors, guests, budget items) that never passes systemEvents
  // is completely unaffected.
  type FeedItem =
    | { kind: 'comment'; ts: number; data: ThreadComment }
    | { kind: 'system'; ts: number; data: SystemThreadEvent };
  // Newest first — matches every other activity/notification feed in the
  // app (e.g. BundlesView's Recent Assignments), so the most relevant entry
  // is always the one visible without scrolling.
  const feed: FeedItem[] = [
    ...comments.map(c => ({ kind: 'comment' as const, ts: new Date(c.createdAtISO).getTime(), data: c })),
    ...(systemEvents ?? []).map(e => ({ kind: 'system' as const, ts: new Date(e.timestampISO).getTime(), data: e })),
  ].sort((a, b) => b.ts - a.ts);

  const handlePost = async () => {
    const body = draft.trim();
    if (!body || posting) return;
    setPosting(true);
    try {
      await onPost(body);
      setDraft('');
    } catch {
      // Keep the draft — the caller surfaces the error to the user.
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="ds-comments">
      {label && <label className="ds-comments-label">{label}</label>}
      <div className="ds-comment-add">
        <div className="ds-comment-input-wrap">
          <textarea
            ref={draftInputRef}
            className="ds-comment-input"
            placeholder={placeholder}
            value={draft}
            onChange={e => handleDraftChange(e.target.value, e.target.selectionStart ?? e.target.value.length)}
            onKeyUp={e => handleDraftChange(draft, e.currentTarget.selectionStart ?? draft.length)}
            onBlur={() => setTimeout(() => setMentionQuery(null), 150)}
            disabled={disabled}
            rows={2}
          />
          {mentionAnchor === 'draft' && mentionQuery !== null && mentionMatches.length > 0 && (
            <ul className="ds-comment-mention-menu">
              {mentionMatches.map(m => (
                <li key={m.id}>
                  <button type="button" onMouseDown={e => { e.preventDefault(); insertMention(m.name); }}>
                    {m.name}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <button
          type="button"
          className="ds-comment-post-btn"
          onClick={handlePost}
          disabled={disabled || !draft.trim() || posting}
        >
          {posting ? 'Posting…' : 'Post'}
        </button>
      </div>
      <div className="ds-comments-list">
        {feed.length === 0 && <p className="ds-comments-empty">{emptyText}</p>}
        {feed.map(item => {
          if (item.kind === 'system') {
            return (
              <div key={item.data.id} className="ds-comment ds-comment--system">
                <div className="ds-comment-header">
                  {item.data.actor && (
                    <span className="ds-comment-author">by {resolveAssigneeName(item.data.actor)}</span>
                  )}
                  <span className="ds-comment-date">{formatDateTime(item.data.timestampISO)}</span>
                </div>
                <span className="ds-comment-body">{item.data.content}</span>
              </div>
            );
          }
          const c = item.data;
          const isMine = !!currentUserEmail && (c.authorEmail ?? c.author).toLowerCase() === currentUserEmail.toLowerCase();
          const isEditing = editingId === c.id;
          return (
            <div key={c.id} className="ds-comment">
              <div className="ds-comment-header">
                <span className="ds-comment-author">{resolveAssigneeName(c.author)}</span>
                <span className="ds-comment-date">
                  {formatDateTime(c.createdAtISO)}
                  {c.updatedAtISO && ' (edited)'}
                </span>
                {isMine && onEdit && !isEditing && (
                  <button type="button" className="ds-comment-edit-btn" title="Edit comment" onClick={() => startEdit(c)}>
                    Edit
                  </button>
                )}
                {isMine && onDelete && (
                  <button
                    type="button"
                    className="ds-comment-delete-btn"
                    title="Delete comment"
                    disabled={deletingId === c.id}
                    onClick={() => handleDelete(c.id)}
                  >
                    {deletingId === c.id ? 'Deleting…' : '×'}
                  </button>
                )}
              </div>
              {isEditing ? (
                <div className="ds-comment-edit">
                  <div className="ds-comment-input-wrap">
                    <textarea
                      ref={editInputRef}
                      className="ds-comment-input"
                      value={editDraft}
                      onChange={e => handleEditChange(e.target.value, e.target.selectionStart ?? e.target.value.length)}
                      onKeyUp={e => handleEditChange(editDraft, e.currentTarget.selectionStart ?? editDraft.length)}
                      onBlur={() => setTimeout(() => setMentionQuery(null), 150)}
                      rows={2}
                      autoFocus
                    />
                    {mentionAnchor === 'edit' && mentionQuery !== null && mentionMatches.length > 0 && (
                      <ul className="ds-comment-mention-menu">
                        {mentionMatches.map(m => (
                          <li key={m.id}>
                            <button type="button" onMouseDown={e => { e.preventDefault(); insertMention(m.name); }}>
                              {m.name}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className="ds-comment-edit-actions">
                    <button type="button" className="ds-comment-post-btn" onClick={() => handleSaveEdit(c.id)} disabled={!editDraft.trim() || saving}>
                      {saving ? 'Saving…' : 'Save'}
                    </button>
                    <button type="button" className="ds-comment-cancel-btn" onClick={cancelEdit} disabled={saving}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="ds-comment-body">{renderBody(c.body)}</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CommentThread;
